/**
 * Basi di conoscenza (ADR-0035 RAG) — vector document stores under the "Dati" area.
 * An operator creates a KB bound to an `embedding` LLM config, opens it to upload
 * documents (extract → chunk → embed) + test retrieval, publishes it, and the AI agents
 * (Ask, and opted-in portal agents) retrieve passages from the PUBLISHED ones.
 *
 * This is the MASTER list: a row per KB → click opens the detail page (documents +
 * internal vector search). No accordion — documents and the test search live on the
 * detail screen where they have room.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpen, ChevronRight, Plus, Trash2 } from "lucide-react";

import { usePermissions } from "@/lib/permissions";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { llmConfigsApi } from "@/lib/api-ai";
import { knowledgeBasesApi } from "@/lib/api-knowledge-bases";

const inputCls = "rounded-md border border-border bg-background px-2 py-1.5 text-sm w-full";
const btnCls =
  "inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-sm hover:bg-muted disabled:opacity-50";

const KEY = ["knowledge-bases"] as const;

export function KnowledgeBasesPage() {
  const { can } = usePermissions();
  const navigate = useNavigate();
  const canWrite = can("knowledge_bases", "write");
  const canDelete = can("knowledge_bases", "delete");
  const qc = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: () => knowledgeBasesApi.list() });
  const [creating, setCreating] = useState(false);

  const remove = useMutation({
    mutationFn: (id: string) => knowledgeBasesApi.remove(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
  const setPublished = useMutation({
    mutationFn: (v: { id: string; published: boolean }) =>
      knowledgeBasesApi.update(v.id, { published: v.published }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });

  const rows = q.data ?? [];

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-lg font-semibold">
            <BookOpen className="h-4 w-4 text-muted-foreground" /> Basi di conoscenza
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Raccolte di documenti vettorializzate per il recupero (RAG). Carica file
            (PDF, Markdown, DOCX, TXT…), provale con il motore di ricerca interno,
            pubblicale, e gli assistenti AI citano i passaggi rilevanti con lo strumento
            «cerca nella conoscenza». Ogni base è legata a un modello di embedding
            (configurato nelle Config LLM).
          </p>
        </div>
        {canWrite && (
          <button type="button" className={btnCls} onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" /> Nuova
          </button>
        )}
      </div>

      {q.isLoading && <p className="text-sm text-muted-foreground">Caricamento…</p>}
      {!q.isLoading && rows.length === 0 && (
        <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          Nessuna base di conoscenza. Creane una per dare ai tuoi agenti dei documenti da
          consultare.
        </p>
      )}

      <div className="space-y-2">
        {rows.map((kb) => (
          <div
            key={kb.id}
            role="button"
            tabIndex={0}
            className="flex w-full cursor-pointer items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-left hover:bg-muted/50"
            onClick={() => navigate(`/knowledge-bases/${kb.id}`)}
            onKeyDown={(e) => {
              if (e.key === "Enter") navigate(`/knowledge-bases/${kb.id}`);
            }}
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-sm font-medium">
                {kb.name}
                {kb.published ? (
                  <span className="rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[11px] text-emerald-600 dark:text-emerald-400">
                    Pubblicata
                  </span>
                ) : (
                  <span className="rounded-full bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                    Bozza
                  </span>
                )}
              </div>
              <div className="truncate text-xs text-muted-foreground">
                {kb.document_count} documenti · {kb.chunk_count} frammenti
                {kb.description ? ` · ${kb.description}` : ""}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {canWrite && (
                <span
                  role="button"
                  tabIndex={0}
                  className={btnCls}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPublished.mutate({ id: kb.id, published: !kb.published });
                  }}
                >
                  {kb.published ? "Ritira" : "Pubblica"}
                </span>
              )}
              {canDelete && (
                <span
                  role="button"
                  tabIndex={0}
                  aria-label="Elimina"
                  className={btnCls}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm(`Eliminare la base «${kb.name}» e tutti i suoi documenti?`))
                      remove.mutate(kb.id);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </span>
              )}
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </div>
          </div>
        ))}
      </div>

      {creating && <CreateDialog onClose={() => setCreating(false)} />}
    </div>
  );
}

function CreateDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [configId, setConfigId] = useState("");
  const configs = useQuery({ queryKey: ["ai", "llm-configs"], queryFn: () => llmConfigsApi.list() });
  const embedding = (configs.data?.configs ?? []).filter((c) => c.kind === "embedding");

  const create = useMutation({
    mutationFn: () =>
      knowledgeBasesApi.create({
        name: name.trim(),
        description: description.trim() || null,
        embedding_config_id: configId,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      onClose();
    },
  });

  const valid = name.trim().length > 0 && configId.length > 0;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">Nuova base di conoscenza</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-3">
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">Nome</span>
              <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">Descrizione (opzionale)</span>
              <input
                className={inputCls}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">Modello di embedding</span>
              <select
                className={inputCls}
                value={configId}
                onChange={(e) => setConfigId(e.target.value)}
              >
                <option value="">Seleziona una config embedding…</option>
                {embedding.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.provider} · {c.model})
                  </option>
                ))}
              </select>
              {!configs.isLoading && embedding.length === 0 && (
                <span className="mt-1 block text-xs text-warning">
                  Nessuna config di tipo «embedding». Creane una in Config LLM (Tipo:
                  Embedding) prima di creare una base.
                </span>
              )}
            </label>
          </div>
        </DialogBody>
        <DialogFooter>
          <button type="button" className={btnCls} onClick={onClose}>
            Annulla
          </button>
          <button
            type="button"
            className={btnCls}
            disabled={!valid || create.isPending}
            onClick={() => create.mutate()}
          >
            Crea
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
